/* pos/types */

import * as t from "io-ts";
import { DataCodec as PosSoftwareDataCodec } from "../possoftware/types";

function tOptional<T extends t.Mixed>(typeThing: T) {
  return t.union([t.undefined, t.null, typeThing]);
}
const tId = t.union([t.undefined, t.null, t.string]);

const ErrorCodec = t.type(
  {
    code: t.string,
    detail: t.string,
    title: t.string,
  },
  "ErrorCodec"
);

const AttributeCodec = t.intersection([
  t.type({
    POSDesc: t.string,
    Interface: t.string,
  }),
  t.partial({
    TID: t.string, // This is actually the selected POIID but named as TID for historical reasons.
    SiteId: t.string, // Used for OPI only
    WSNo: t.string, // Used for OPI only
    TimeDiff: t.number, // Time difference between POS and server.
    LoggedOut: t.string, // If present, the date/time of the last logout.
    POIID: t.array(t.string), // List of allowed POIID that this POS can pair with
    // POSSoftware: t.array(PosSoftwareDataCodec), // id of POSSoftware used
    POSSoftware: t.string, // id of POSSoftware used
    POSSoftwarePresentation: PosSoftwareDataCodec,
    TerminalEnvironment: t.string, // The environment the terminal is running under: Attended, Unattended, SemiAttended. Updated every login.
    SaleCapabilities: t.array(t.string), // The capabilities of the Sale System
    TotalsGroupID: t.string, // The default Totals Group ID to use for reconciliation purposes.
    OperatorID: t.string, // The default operator ID to use for reconciliation purposes.
    ShiftNumber: t.string, // The default shift number to use for reconciliation purposes.
    PrintOnPOS: t.boolean,
    PrintOnTerminal: t.boolean,
    AuthenticationType: t.string,
    KEK: t.string,
    KEKIdentifier: t.string,
    KEKVersion: t.string,
  }),
]);

const DataCodec = t.intersection([
  t.type({
    id: tId,
    type: t.string,
    attributes: AttributeCodec,
  }),
  t.partial({
    createdAt: t.string,
    updatedAt: t.string,
  }),
]);

export type Data = t.TypeOf<typeof DataCodec>;

const MetaCodec = t.type({
  totalObjects: tOptional(t.number),
});
export const PosCodec = t.type(
  {
    errors: tOptional(t.union([t.undefined, t.array(ErrorCodec)])),
    data: tOptional(t.array(DataCodec)),
    meta: tOptional(MetaCodec),
  },
  "PosCodec"
);

export type Pos = t.TypeOf<typeof PosCodec>;

export enum PosActionTypes {
  FETCH = "@@pos/FETCH",
  FETCH_SUCCESS = "@@pos/FETCH_SUCCESS",
  FETCH_ERROR = "@@pos/FETCH_ERROR",
  SAVE_REQUEST = "@@pos/SAVE_REQUEST",
  SAVE_SUCCESS = "@@pos/SAVE_SUCCESS",
  SAVE_ERROR = "@@pos/SAVE_ERROR",
  DELETE_REQUEST = "@@pos/DELETE_REQUEST",
  DELETE_SUCCESS = "@@pos/DELETE_SUCCESS",
  DELETE_ERROR = "@@pos/DELETE_ERROR",
  DOWNLOAD_REQUEST = "@@pos/DOWNLOAD_REQUEST",
}

export interface PosState {
  readonly loading: boolean;
  readonly data: Pos | undefined;
  readonly errors?: string;
}
